import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import useStyles from "./TextAnswerRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { QuestionType } from "../../../models/enums/QuestionType";
import { removeHtmlTags } from "../../../utils/index";
import { QuestionModel } from "../../../models/QuestionModel";
import InputBox from "../../shared/InputBox";
import useQuestion from "../../../hooks/useQuestion";
import useTranslatable from "../../../hooks/useTranslatable";
import { getSettingString } from "../../../models/utilities/settingUtilities";
import { size } from "../../../models/utilities/questionUtilities";

interface Props extends RendererProps {
    multiline?: boolean;
    inputType?: string;
    placeholder?: string;
    inputDivRef?: React.LegacyRef<HTMLDivElement>;
    inputRef?: React.LegacyRef<HTMLInputElement & HTMLTextAreaElement>;
    min?: number;
    max?: number;
    maxLength?: number;
    onBlur?: React.FocusEventHandler;
    onFocus?: React.FocusEventHandler;
    onKeyDown?: React.KeyboardEventHandler;
    onKeyPress?: React.KeyboardEventHandler;
    onChange?: React.ChangeEventHandler;
    openDatePicker?(): void;
    value?: string;
    disabled?: boolean;
    lines?: number;
}

function hasDatePicker(question: QuestionModel) {
    return ["Date", "DateTime", "Time"].includes(getSettingString(question, "Type"));
}

export default function TextAnswerRenderer(props: Props) {
    const [focused, setFocused] = React.useState(false);

    const {
        gridRendering,
        multiline,
        inputType,
        placeholder,
        inputRef,
        min,
        max,
        maxLength,
        onBlur,
        onFocus,
        onKeyDown,
        onKeyPress,
        onChange,
        value,
        disabled,
        lines,
    } = props;

    const theme = useTheme();
    const classes = useStyles({ theme, gridRendering });
    const question = useQuestion();
    const questionText = useTranslatable(question, "TEXT");
    const leftLabel = useTranslatable(question, "LeftLabel");
    const rightLabel = useTranslatable(question, "RightLabel");
    const questionId = question.parent ? question.parent.elementId : question.elementId;
    const [variable] = question.variables;

    const classNames = classnames(
        classes.textAnswer,
        multiline && classes.textAnswerMultiLine,
        question.type === QuestionType.NumericAnswer && classes.textAnswerNumeric,
        question.type === QuestionType.DateTimeAnswer && classes.textAnswerDateTime,
    );

    const inputOnBlur = (e) => {
        setFocused(false);
        if (onBlur) {
            onBlur(e);
        }
    };

    const inputOnFocus = (e) => {
        setFocused(true);
        if (onFocus) {
            onFocus(e);
        }
    };

    const accessory: IconProp = hasDatePicker(question) ? ["far", "calendar-alt"] : null;

    return (
        <InputBox
            focused={focused}
            freeform={multiline}
            size={size(question)}
            leftLabel={leftLabel}
            rightLabel={rightLabel}
            className={classNames}
            accessoryRight={accessory}
        >
            {multiline && (
                <textarea
                    id={`${variable.elementId}_INPUT`}
                    aria-label={removeHtmlTags(questionText)}
                    aria-labelledby={`${questionId}_LABEL`}
                    name={variable.elementId}
                    rows={lines || 2}
                    placeholder={placeholder}
                    disabled={disabled}
                    ref={inputRef}
                    maxLength={maxLength}
                    onBlur={inputOnBlur}
                    onFocus={inputOnFocus}
                    onKeyDown={onKeyDown}
                    onKeyPress={onKeyPress}
                    onChange={onChange}
                    value={value ?? undefined}
                />
            )}
            {!multiline && (
                <input
                    type={inputType}
                    id={`${variable.elementId}_INPUT`}
                    aria-label={removeHtmlTags(questionText)}
                    aria-labelledby={`${questionId}_LABEL`}
                    name={variable.elementId}
                    placeholder={placeholder}
                    ref={inputRef}
                    min={min}
                    max={max}
                    maxLength={maxLength}
                    onBlur={inputOnBlur}
                    onFocus={inputOnFocus}
                    onKeyDown={onKeyDown}
                    onKeyPress={onKeyPress}
                    onChange={onChange}
                    disabled={disabled}
                    value={value ?? undefined}
                    autoComplete="off"
                    style={{
                        textAlign: inputType === "tel" ? "right" : "inherit",
                    }}
                />
            )}
        </InputBox>
    );
}
